// MobX Store for Map functionality
import { makeAutoObservable } from "mobx";
import { DEFAULT_MAP_CONFIG, } from "../types/map";
export class MapStore {
    constructor(mapService) {
        this.mapService = mapService;
        // Observable state
        this.isInitialized = false;
        this.isLoading = false;
        this.error = null;
        this.config = DEFAULT_MAP_CONFIG;
        this.containerSize = { width: "100%", height: "400px" };
        this.routesById = new Map();
        this.locatorsById = new Map();
        // Map service instance
        this.mapContainer = null;
        makeAutoObservable(this);
    }
    // Map initialization
    async initializeMap(container, config) {
        this.setLoading(true);
        this.clearError();
        try {
            this.mapContainer = container;
            if (config) {
                this.updateConfig(config);
            }
            await this.mapService.initializeMap(container, this.config);
            this.setInitialized(true);
            // Load existing locators and routes
            await this.reloadMapData();
        }
        catch (error) {
            this.setError("Не удалось инициализировать карту");
            console.error("Map initialization error:", error);
        }
        finally {
            this.setLoading(false);
        }
    }
    // Locator management
    async addLocator(locator) {
        this.setLoading(true);
        this.clearError();
        try {
            await this.mapService.addLocator(locator);
            this.addLocatorToState(locator);
        }
        catch (error) {
            this.setError("Не удалось добавить локатор на карту");
            console.error("Add locator error:", error);
        }
        finally {
            this.setLoading(false);
        }
    }
    async updateLocator(id, updates) {
        this.setLoading(true);
        this.clearError();
        try {
            await this.mapService.updateLocator(id, updates);
            this.updateLocatorInState(id, updates);
        }
        catch (error) {
            this.setError("Не удалось обновить локатор");
            console.error("Update locator error:", error);
        }
        finally {
            this.setLoading(false);
        }
    }
    async removeLocator(id) {
        this.setLoading(true);
        this.clearError();
        try {
            await this.mapService.removeLocator(id);
            this.removeLocatorFromState(id);
        }
        catch (error) {
            this.setError("Не удалось удалить локатор");
            console.error("Remove locator error:", error);
        }
        finally {
            this.setLoading(false);
        }
    }
    async clearAllLocators() {
        this.setLoading(true);
        this.clearError();
        try {
            await this.mapService.clearAllLocators();
            this.clearLocatorsState();
        }
        catch (error) {
            this.setError("Не удалось очистить локаторы");
            console.error("Clear locators error:", error);
        }
        finally {
            this.setLoading(false);
        }
    }
    // Route management
    async addRoute(route) {
        this.setLoading(true);
        this.clearError();
        try {
            await this.mapService.addRoute(route);
            this.addRouteToState(route);
        }
        catch (error) {
            this.setError("Не удалось добавить маршрут на карту");
            console.error("Add route error:", error);
        }
        finally {
            this.setLoading(false);
        }
    }
    async updateRoute(id, updates) {
        this.setLoading(true);
        this.clearError();
        try {
            await this.mapService.updateRoute(id, updates);
            this.updateRouteInState(id, updates);
        }
        catch (error) {
            this.setError("Не удалось обновить маршрут");
            console.error("Update route error:", error);
        }
        finally {
            this.setLoading(false);
        }
    }
    async removeRoute(id) {
        this.setLoading(true);
        this.clearError();
        try {
            await this.mapService.removeRoute(id);
            this.removeRouteFromState(id);
        }
        catch (error) {
            this.setError("Не удалось удалить маршрут");
            console.error("Remove route error:", error);
        }
        finally {
            this.setLoading(false);
        }
    }
    async clearAllRoutes() {
        this.setLoading(true);
        this.clearError();
        try {
            await this.mapService.clearAllRoutes();
            this.clearRoutesState();
        }
        catch (error) {
            this.setError("Не удалось очистить маршруты");
            console.error("Clear routes error:", error);
        }
        finally {
            this.setLoading(false);
        }
    }
    // Map controls
    setCenter(position) {
        try {
            this.mapService.setCenter(position);
        }
        catch (error) {
            console.error("Set center error:", error);
        }
    }
    setZoom(zoom) {
        try {
            this.mapService.setZoom(zoom);
            this.updateConfig({ zoom });
        }
        catch (error) {
            console.error("Set zoom error:", error);
        }
    }
    fitBounds(points) {
        try {
            this.mapService.fitBounds(points);
        }
        catch (error) {
            console.error("Fit bounds error:", error);
        }
    }
    resizeMap() {
        try {
            this.mapService.resize();
        }
        catch (error) {
            console.error("Resize error:", error);
        }
    }
    // Geocoding — NOT_IMPLEMENTED: delegates to MapService stubs that return null
    async geocodeAddress(address) {
        this.setLoading(true);
        this.clearError();
        try {
            const result = await this.mapService.geocodeAddress(address);
            return result;
        }
        catch (error) {
            this.setError("Не удалось найти адрес");
            console.error("Geocoding error:", error);
            return null;
        }
        finally {
            this.setLoading(false);
        }
    }
    async reverseGeocode(position) {
        this.setLoading(true);
        this.clearError();
        try {
            const result = await this.mapService.reverseGeocode(position);
            return result;
        }
        catch (error) {
            this.setError("Не удалось определить адрес");
            console.error("Reverse geocoding error:", error);
            return null;
        }
        finally {
            this.setLoading(false);
        }
    }
    async buildRoute(points) {
        this.setLoading(true);
        this.clearError();
        try {
            const routePoints = await this.mapService.buildRoute(points);
            if (routePoints) {
                const route = {
                    id: `route_${Date.now()}`,
                    points: routePoints,
                    title: "Построенный маршрут",
                    isVisible: true,
                };
                await this.addRoute(route);
            }
        }
        catch (error) {
            this.setError("Не удалось построить маршрут");
            console.error("Build route error:", error);
        }
        finally {
            this.setLoading(false);
        }
    }
    // Container size management
    setContainerSize(size) {
        this.containerSize = size;
        // Trigger resize after container size change
        setTimeout(() => {
            this.resizeMap();
        }, 100);
    }
    // Configuration management
    updateConfig(updates) {
        this.config = { ...this.config, ...updates };
    }
    // Private state management methods
    setLoading(loading) {
        this.isLoading = loading;
    }
    setInitialized(initialized) {
        this.isInitialized = initialized;
    }
    setError(error) {
        this.error = error;
    }
    clearError() {
        this.error = null;
    }
    // Public method to clear errors (for UI components)
    clearMapError() {
        this.clearError();
    }
    addLocatorToState(locator) {
        this.locatorsById.set(locator.id, locator);
    }
    updateLocatorInState(id, updates) {
        const cur = this.locatorsById.get(id);
        if (!cur)
            return;
        this.locatorsById.set(id, { ...cur, ...updates });
    }
    removeLocatorFromState(id) {
        this.locatorsById.delete(id);
    }
    clearLocatorsState() {
        this.locatorsById.clear();
    }
    addRouteToState(route) {
        this.routesById.set(route.id, route);
    }
    updateRouteInState(id, updates) {
        const cur = this.routesById.get(id);
        if (!cur)
            return;
        this.routesById.set(id, { ...cur, ...updates });
    }
    removeRouteFromState(id) {
        this.routesById.delete(id);
    }
    clearRoutesState() {
        this.routesById.clear();
    }
    // Reload map data (useful after reconnection)
    async reloadMapData() {
        try {
            // Reload all locators
            for (const locator of this.locators) {
                await this.mapService.addLocator(locator);
            }
            // Reload all routes
            for (const route of this.routes) {
                await this.mapService.addRoute(route);
            }
        }
        catch (error) {
            console.error("Error reloading map data:", error);
        }
    }
    // Computed values
    get hasLocators() {
        return this.locators.length > 0;
    }
    get routes() {
        return Array.from(this.routesById.values());
    }
    get locators() {
        return Array.from(this.locatorsById.values());
    }
    get hasRoutes() {
        return this.routes.length > 0;
    }
    get visibleLocators() {
        return this.locators.filter((locator) => locator.isVisible !== false);
    }
    get visibleRoutes() {
        return this.routes.filter((route) => route.isVisible !== false);
    }
    get totalPoints() {
        return this.routes.reduce((total, route) => total + route.points.length, 0);
    }
    get allMapPoints() {
        const locatorPoints = this.locators.map((l) => l.position);
        const routePoints = this.routes.flatMap((r) => r.points);
        return [...locatorPoints, ...routePoints];
    }
    // Utility methods
    getLocatorById(id) {
        return this.locators.find((locator) => locator.id === id);
    }
    getRouteById(id) {
        return this.routesById.get(id);
    }
    // Cleanup method
    destroy() {
        this.mapService.destroy();
        this.setInitialized(false);
        this.clearLocatorsState();
        this.clearRoutesState();
        this.clearError();
        this.mapContainer = null;
    }
}
